import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import Tab from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Block, { BlockProps } from "@store/Components/Common/Block";
import ResponsiveButton from "@store/Components/Common/ResponsiveButton";
import StoreProductCarousel from "@store/Components/Store/StoreProductCarousel";
import { useLinksTo } from "@store/Hooks/Common";
import { useProductsForProductInfoListCollection } from "@store/Store/Components/ProductInfoList/Hooks";
import { COMMON } from "@store/theme/design";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum categoryFields {
    label = "label",
    categoryIds = "categoryIds",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
}

interface CategoryModel {
    fields: {
        [categoryFields.label]: string;
        [categoryFields.categoryIds]: string[];
        [categoryFields.buttonLabel]: string;
        [categoryFields.buttonLink]: LinkFieldValue;
    };
}

const enum fields {
    title = "title",
    categories = "categories",
    count = "count",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.categories]: CategoryModel[];
        [fields.count]: number;
    };
}

const mapDispatchToProps = {
    loadProductInfoList,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface NewArrivalsStyles {
    block?: BlockProps;
    title?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
    buttonWrapper?: InjectableCss;
}

export const newArrivalsStyles: NewArrivalsStyles = {
    block: {
        margin: COMMON.spacing.spacedContent,
    },
    title: {
        variant: "h2",
        css: css`
            text-align: center;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    button: {
        variant: "secondary",
        css: css`
            ${props => props.theme?.button?.secondary?.css}
            margin-top: 50px;
        `,
    },
};

const styles = newArrivalsStyles;

interface NewArrivalsTabModel {
    categoryId: string;
    label: string;
    products?: ProductModel[];
    button?: {
        label?: string;
        url?: string;
        props?: ButtonProps;
    };
}

const getId = (id: string, categoryId: string, count: number) => {
    return `${id}.${categoryId}.${count}`;
};

const getTabs = (id: string, categories: CategoryModel[], count: number) => {
    const ids: string[] = [];
    const links: LinkFieldValue[] = [];
    const tabs = categories.reduce((result: NewArrivalsTabModel[], category) => {
        const {
            fields: { categoryIds, label, buttonLabel, buttonLink },
        } = category;
        const categoryId = categoryIds[0];

        if (!categoryId || result.some(x => x.categoryId === categoryId)) {
            return result;
        }

        ids.push(getId(id, categoryId, count));
        links.push(buttonLink);

        const tab: NewArrivalsTabModel = {
            categoryId,
            label,
            button: {
                label: buttonLabel,
            },
        };

        return [...result, tab];
    }, []);

    const productsById = useProductsForProductInfoListCollection(ids);
    const buttonLinks = useGetLinks(links, x => x || {});
    const buttonClicks = useLinksTo(buttonLinks.map(x => x.url));

    tabs.forEach((x, index) => {
        const link = buttonLinks[index];

        x.products = productsById[getId(id, x.categoryId, count)];
        x.button = link.url
            ? {
                  label: x.button?.label || link.title || link.url,
                  url: link.url,
                  props: {
                      ...styles.button,
                      onClick: buttonClicks[index],
                  },
              }
            : undefined;
    });

    return tabs;
};

const NewArrivals: FC<Props> = ({ id, fields: { title, categories, count }, loadProductInfoList }) => {
    const shellConttext = useContext(ShellContext);
    const [currentTab, setCurrentTab] = useState<NewArrivalsTabModel>();
    const [cachedIds, setCachedIds] = useState<string[]>([]);
    const tabs = getTabs(id, categories, count);

    useEffect(() => {
        if (tabs.length) {
            setCurrentTab(tabs[0]);
        }
    }, [categories]);

    useEffect(() => {
        if (currentTab && count) {
            const categoryId = currentTab.categoryId;
            const noCache = !cachedIds.includes(categoryId);
            if (!currentTab.products || noCache) {
                if (noCache) {
                    setCachedIds([...cachedIds, categoryId]);
                }

                loadProductInfoList({
                    id: getId(id, currentTab.categoryId, count),
                    getProductCollectionParameter: {
                        filter: "newArrivals",
                        categoryId,
                        pageSize: count,
                        includeProductsInSubCategories: true,
                    } as any,
                });
            }
        }
    }, [currentTab, count]);

    if (!shellConttext.isEditing && !tabs.length) {
        return null;
    }

    const onTabChange = (event: React.MouseEvent | React.KeyboardEvent) => {
        const categoryId = event.currentTarget.getAttribute("data-category-id");
        setCurrentTab(tabs.find(x => x.categoryId === categoryId));
    };

    return (
        <Block {...styles.block}>
            {title ? <Typography {...styles.title}>{title}</Typography> : null}
            {tabs.length ? (
                <TabGroup onTabChange={onTabChange} current="0">
                    {tabs.map(({ categoryId, label, products, button }, index) => (
                        <Tab headline={label} tabKey={index.toString()} key={categoryId} data-category-id={categoryId}>
                            <StoreProductCarousel id={getId(id, categoryId, count)} products={products} />
                            {button ? (
                                <StyledWrapper {...styles.buttonWrapper}>
                                    <ResponsiveButton buttonProps={button.props}>{button.label}</ResponsiveButton>
                                </StyledWrapper>
                            ) : null}
                        </Tab>
                    ))}
                </TabGroup>
            ) : null}
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(NewArrivals),
    definition: {
        group: "Common",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.count,
                displayName: "Products Count",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 8,
                isRequired: true,
            },
            {
                name: fields.categories,
                displayName: "Categories",
                editorTemplate: "ListField",
                fieldType: "Translatable",
                defaultValue: [],
                getDisplay: (item: HasFields) => item.fields.label,
                fieldDefinitions: [
                    {
                        name: categoryFields.label,
                        displayName: "Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: categoryFields.categoryIds,
                        displayName: "Category",
                        editorTemplate: "CategoriesField",
                        defaultValue: [],
                        isRequired: true,
                        singleCategorySelection: () => true,
                    },
                    {
                        name: categoryFields.buttonLink,
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: categoryFields.buttonLabel,
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.buttonLink?.value,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
